import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import axios from 'axios';

const emptyForm = {
  id: '',
  title: '',
  start: '',
  end: '',
  location: '',
  audience: '',
  partner: '',
  description: '',
  applyLink: '',
  status: 'ACTIVE',
};

const toInputDateTime = (value) => (value ? String(value).slice(0, 16) : '');

const TrainingProgramsManager = () => {
  const token = localStorage.getItem('token');
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPrograms = useCallback(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/2.0/training-programs?includeInactive=true`)
      .then((res) => setPrograms(res.data.data || []))
      .catch((err) => {
        console.error('Error loading training programs', err);
        alert('Unable to load training programs');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const saveProgram = (event) => {
    event.preventDefault();
    setSaving(true);

    const request = form.id
      ? axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/admin/training-programs/${form.id}`, form, { headers })
      : axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/training-programs`, form, { headers });

    request
      .then(() => {
        alert('Training program saved');
        setForm(emptyForm);
        fetchPrograms();
      })
      .catch((err) => {
        console.error('Error saving training program', err);
        alert(err?.response?.data?.message || 'Unable to save training program');
      })
      .finally(() => setSaving(false));
  };

  const deleteProgram = (id) => {
    if (!window.confirm('Delete this training program?')) return;

    axios
      .delete(`${process.env.REACT_APP_API_BASE_URL}/api/admin/training-programs/${id}`, { headers })
      .then(() => {
        alert('Training program deleted');
        fetchPrograms();
      })
      .catch((err) => {
        console.error('Error deleting training program', err);
        alert(err?.response?.data?.message || 'Unable to delete training program');
      });
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Training Programs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add workshops and training sessions that appear on the public calendar.
      </Typography>

      <Box component="form" onSubmit={saveProgram} sx={{ display: 'grid', gap: 2, maxWidth: 760, mb: 3 }}>
        <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required size="small" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField label="Start" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} required size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="End" type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} size="small" />
          <TextField label="Audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} size="small" />
          <TextField label="Partner" value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} size="small" />
          <TextField label="Status" select SelectProps={{ native: true }} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} size="small">
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
          </TextField>
        </Box>
        <TextField label="Apply Link" value={form.applyLink} onChange={(e) => setForm({ ...form, applyLink: e.target.value })} size="small" />
        <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline minRows={3} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : form.id ? 'Update Program' : 'Add Program'}
          </Button>
          {form.id && (
            <Button variant="outlined" onClick={() => setForm(emptyForm)}>
              Cancel Edit
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {programs.map((program) => (
          <Box key={program.id} sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
            <Typography><strong>{program.title}</strong></Typography>
            <Typography variant="body2">{program.start} {program.end ? `to ${program.end}` : ''}</Typography>
            <Typography variant="body2">{program.partner} | {program.audience} | {program.location}</Typography>
            <Typography variant="body2">Status: {program.status}</Typography>
            {program.applyLink && <Typography variant="body2">Apply: {program.applyLink}</Typography>}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" onClick={() => setForm({
                ...program,
                start: toInputDateTime(program.start),
                end: toInputDateTime(program.end),
              })}>
                Edit
              </Button>
              <Button size="small" color="error" onClick={() => deleteProgram(program.id)}>
                Delete
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TrainingProgramsManager;
